import {
  DISABLED_HIGHER_BETTER_THRESHOLD_VALUE,
  DISABLED_LOWER_BETTER_THRESHOLD_VALUE,
  metricThresholdsExecutionRuns,
  metricThresholdsCoverageDirectories,
  metricThresholdsCoverageFiles,
  metricThresholdsCoverageRuns,
  metricThresholdsJobs,
  metricThresholdsPipelines,
  metricThresholdsStages,
  metricThresholdsSuites,
  metricThresholdsTests,
} from '@/shared/utils/metricsThresholds';
import type {
  CoverageMetricThresholdGroup,
  MetricThresholdGroup,
  ProjectMetricThresholds,
} from '@flowqa/shared';

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

export type ProjectMetricThresholdUpdate = DeepPartial<ProjectMetricThresholds>;

export const defaultProjectMetricThresholds: ProjectMetricThresholds = {
  suites: metricThresholdsSuites,
  executionRuns: metricThresholdsExecutionRuns,
  tests: metricThresholdsTests,
  pipelines: metricThresholdsPipelines,
  stages: metricThresholdsStages,
  jobs: metricThresholdsJobs,
  coverageRuns: metricThresholdsCoverageRuns,
  directories: metricThresholdsCoverageDirectories,
  files: metricThresholdsCoverageFiles,
};

const isDisabledSentinelThreshold = (
  threshold: MetricThresholdGroup[keyof MetricThresholdGroup],
): boolean => {
  const disabledValue =
    threshold.direction === 'higherBetter'
      ? DISABLED_HIGHER_BETTER_THRESHOLD_VALUE
      : DISABLED_LOWER_BETTER_THRESHOLD_VALUE;
  return threshold.target === disabledValue && threshold.warn === disabledValue;
};

const mergeMetricThreshold = <TThreshold extends MetricThresholdGroup[keyof MetricThresholdGroup]>(
  base: TThreshold,
  update?: DeepPartial<TThreshold>,
): TThreshold => {
  const merged = { ...base, ...(update ?? {}) };
  if (isDisabledSentinelThreshold(merged)) {
    return { ...merged, target: base.target, warn: base.warn, enabled: false };
  }
  return merged;
};

export const mergeProjectThresholdGroup = (
  base: MetricThresholdGroup,
  update?: DeepPartial<MetricThresholdGroup>,
): MetricThresholdGroup => ({
  passRate: mergeMetricThreshold(base.passRate, update?.passRate),
  frequency: mergeMetricThreshold(base.frequency, update?.frequency),
  avgDuration: mergeMetricThreshold(base.avgDuration, update?.avgDuration),
  flakeScore: mergeMetricThreshold(base.flakeScore, update?.flakeScore),
  mttf: mergeMetricThreshold(base.mttf, update?.mttf),
  greenTime: mergeMetricThreshold(base.greenTime, update?.greenTime),
});

export const mergeProjectCoverageThresholdGroup = (
  base: CoverageMetricThresholdGroup,
  update?: DeepPartial<CoverageMetricThresholdGroup>,
): CoverageMetricThresholdGroup => ({
  line: { ...base.line, ...(update?.line ?? {}) },
  function: { ...base.function, ...(update?.function ?? {}) },
  branch: { ...base.branch, ...(update?.branch ?? {}) },
  statement: { ...base.statement, ...(update?.statement ?? {}) },
});

export const mergeProjectThresholds = (
  source?: ProjectMetricThresholdUpdate,
): ProjectMetricThresholds => ({
  suites: mergeProjectThresholdGroup(defaultProjectMetricThresholds.suites, source?.suites),
  executionRuns: mergeProjectThresholdGroup(
    defaultProjectMetricThresholds.executionRuns,
    source?.executionRuns,
  ),
  tests: mergeProjectThresholdGroup(defaultProjectMetricThresholds.tests, source?.tests),
  pipelines: mergeProjectThresholdGroup(
    defaultProjectMetricThresholds.pipelines,
    source?.pipelines,
  ),
  stages: mergeProjectThresholdGroup(defaultProjectMetricThresholds.stages, source?.stages),
  jobs: mergeProjectThresholdGroup(defaultProjectMetricThresholds.jobs, source?.jobs),
  coverageRuns: mergeProjectCoverageThresholdGroup(
    defaultProjectMetricThresholds.coverageRuns,
    source?.coverageRuns,
  ),
  directories: mergeProjectCoverageThresholdGroup(
    defaultProjectMetricThresholds.directories,
    source?.directories,
  ),
  files: mergeProjectCoverageThresholdGroup(defaultProjectMetricThresholds.files, source?.files),
});

const disableMetricThresholdGroup = (group: MetricThresholdGroup): MetricThresholdGroup => ({
  passRate: { ...group.passRate, enabled: false },
  frequency: { ...group.frequency, enabled: false },
  avgDuration: { ...group.avgDuration, enabled: false },
  flakeScore: { ...group.flakeScore, enabled: false },
  mttf: { ...group.mttf, enabled: false },
  greenTime: { ...group.greenTime, enabled: false },
});

export const applyTestingMetricsEnabled = (
  thresholds: ProjectMetricThresholds,
  testingMetricsEnabled: boolean,
): ProjectMetricThresholds => {
  if (testingMetricsEnabled) return thresholds;

  return {
    ...thresholds,
    executionRuns: disableMetricThresholdGroup(thresholds.executionRuns),
    suites: disableMetricThresholdGroup(thresholds.suites),
    tests: disableMetricThresholdGroup(thresholds.tests),
  };
};
